import "../../../../css/GlobalStyles.css";
import { useCallback, useContext, useMemo, useState } from "react";
import { Event, EventType } from "@markit/common.types";
import {
  foundUserGenericTrackingLinks,
  hasSubscription,
} from "@markit/common.utils";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../../redux/slices/accountSlice";
import { getEventState } from "../../../../redux/slices/eventSlice";
import EmptyStateButton from "../../../../components/Buttons/EmptyStateButton";
import { Icon } from "@iconify/react";
import { Colors } from "../../../../utils/colors";
import RectangleButtonCreatorPanel from "../../../../components/Buttons/RectangleButtonCreatorPanel";
import MarkitPlusActionButton from "../../../../components/Subscription/MarkitPlusActionButton";
import { MixpanelContext } from "../../../../context/AnalyticsService";
import CreatorModePanelWrapper from "../../../../components/CreatorDashboard/CreatorModePanelWrapper";
import CreateFormAndTrackingLinkModal from "../../../../components/CreatorDashboard/FormSubmissions/CreateFormAndTrackingLinkModal";
import { HorizontalDivider } from "../../../../components/Dividers/HorizontalDivider";
import { GenericTrackingLinkItem } from "../../../../components/DisplayItem/GenericTrackingLinkItem";
import FlatlistLoadMore from "../../../../components/FlatlistLoadMore";
import filter from "lodash.filter";
import SearchBoxContainer from "../../../../components/Containers/SearchBoxContainer";
import { EmptyStateFlatlist } from "../../../../components/EmptyStates/EmptyStateFlatlist";

// Tracking Links Creator Panel
const TrackingLinksPanel = () => {
  const { account } = useSelector(getAccountState);
  const { accountData } = account;
  const mixpanel = useContext(MixpanelContext);
  const { events: eventList } = useSelector(getEventState);
  const { events } = eventList;
  const [value, setValue] = useState(0);
  const [createTrackingLinkVisible, setCreateTrackingLinkVisible] =
    useState(false);
  const [existingTrackingLink, setExistingTrackingLink] = useState<
    Event | undefined
  >(undefined);
  const [windowSize, setWindowSize] = useState(15);
  const [searchTerm, setSearchTerm] = useState("");

  const activeTrackingLinks = useMemo(
    () => foundUserGenericTrackingLinks(events, accountData.uid, "active"),
    [accountData.uid, events]
  );

  const inactiveTrackingLinks = useMemo(
    () => foundUserGenericTrackingLinks(events, accountData.uid, "inactive"),
    [accountData.uid, events]
  );

  const contains = useCallback((trackingLink: Event, query: string) => {
    return trackingLink.title.toLowerCase().includes(query);
  }, []);

  const handleSearch = useCallback((text: string) => {
    setSearchTerm(text.toLowerCase());
  }, []);

  const currentItemsToShow: Event[] = useMemo(() => {
    let items: Event[] =
      value === 0 ? activeTrackingLinks : inactiveTrackingLinks;
    if (searchTerm !== "") {
      items = filter(items, (item: Event) => {
        return contains(item, searchTerm);
      });
    }
    return items;
  }, [value, activeTrackingLinks, inactiveTrackingLinks, searchTerm, contains]);

  const createTrackingLinkOnPress = useCallback(() => {
    mixpanel.track("Pressed Create Generic Tracking Link", {
      distinct_id: accountData.uid,
      platform: "webapp",
      button_location: "tracking_links_panel",
    });
    setCreateTrackingLinkVisible(true);
  }, [accountData.uid, mixpanel]);

  const renderSubPanel = useMemo(() => {
    const renderLinks = (trackingLinks: Event[]) => {
      return (
        <FlatlistLoadMore
          fullList={trackingLinks}
          currentList={trackingLinks.slice(0, windowSize)}
          renderItem={(trackingLink: Event) => (
            <div className="ColumnNormal">
              <GenericTrackingLinkItem
                key={trackingLink.id}
                item={trackingLink}
                setExistingTrackingLink={(link: Event) => {
                  setExistingTrackingLink(link);
                  setCreateTrackingLinkVisible(true);
                }}
              />
              <HorizontalDivider altMargin={20} />
            </div>
          )}
          renderWhenEmpty={
            <EmptyStateFlatlist
              searchTerm={searchTerm}
              isLoading={false}
              containerStyles={{ paddingTop: 120 }}
              nonSearchEmptyView={
                <EmptyStateButton
                  title={
                    value === 0
                      ? "No Active Tracking Links"
                      : "No Inactive Tracking Links"
                  }
                  description={`Your ${
                    value === 0 ? "active" : "inactive"
                  } tracking links will show up here`}
                  icon={
                    <Icon
                      icon={"mdi:link-variant"}
                      height={63}
                      style={{ color: Colors.BLACK }}
                    />
                  }
                  iconBackgroundColor={Colors.WHITE1}
                  iconBox={80}
                  btnText="New Tracking Link"
                  onPress={createTrackingLinkOnPress}
                  containerStyles={{ paddingTop: 200 }}
                />
              }
            />
          }
          isLoadingMore={false}
          showLoadMore={windowSize < trackingLinks.length}
          loadMoreOnPress={() => setWindowSize(windowSize + 15)}
        />
      );
    };

    return (
      <div className="ColumnNormal" style={{ gap: "14px", paddingBottom: 100 }}>
        {renderLinks(currentItemsToShow)}
      </div>
    );
  }, [
    currentItemsToShow,
    windowSize,
    searchTerm,
    value,
    createTrackingLinkOnPress,
  ]);

  return (
    <CreatorModePanelWrapper
      title="Tracking Links"
      headerRight={
        <div className="AlignedRow" style={{ gap: 14 }}>
          {!hasSubscription(accountData) ? <MarkitPlusActionButton /> : null}
          <RectangleButtonCreatorPanel
            title="New Link"
            iconName="ion:create"
            onPress={createTrackingLinkOnPress}
          />
        </div>
      }
      headerTabs={{
        tabNames: [
          `Active (${activeTrackingLinks.length})`,
          `Inactive (${inactiveTrackingLinks.length})`,
        ],
        tabValue: value,
        setTabValue: setValue,
      }}
    >
      <div
        className="ColumnNormal"
        style={{
          padding: 30,
          height: "100%",
          backgroundColor: Colors.WHITE,
          gap: 24,
        }}
      >
        <SearchBoxContainer
          placeholder={"Search"}
          onChange={(e) => handleSearch(e.target.value)}
          containerStyles={{ marginTop: 0, width: 394 }}
        />
        {renderSubPanel}
      </div>
      {createTrackingLinkVisible ? (
        <CreateFormAndTrackingLinkModal
          isVisible={createTrackingLinkVisible}
          setIsVisible={(isVisible: boolean) => {
            setCreateTrackingLinkVisible(isVisible);
            setExistingTrackingLink(undefined);
          }}
          existingForm={existingTrackingLink}
          eventType={EventType.GENERIC_LINK_NO_DATA}
        />
      ) : null}
    </CreatorModePanelWrapper>
  );
};

export default TrackingLinksPanel;
